/*
 * Copyright (c) 2025. Slimme Infrasoftware B.V. Apeldoorn /
 * Spin in het Web B.V. Apeldoorn.
 * Deze software mag alleen gebruikt / bekeken / gewijzigd worden
 * volgens de voorwaarden die zijn vastgelegd in de overeenkomst
 * tussen ATR en Slimme Infrasoftware B.V. en de onderliggende
 * overeenkomsten tussen Slimme Infrasoftware B.V en
 * Spin in het Web B.V.
 */

/*
 * filterregel-personeelslabel-edit.component.ts
 * Gegenereerd met Junie (AI), 08-10-2025
 * Auteur: Jelmer Jellema - Spin in het Web B.V
 * Editorcomponent voor personeelslabel-regels met multiselect op labelnamen.
 */

import {ChangeDetectionStrategy, Component, Input, OnInit} from '@angular/core';
import {CommonModule} from '@angular/common';
import {FormsModule} from '@angular/forms';
import {NgSelectModule} from '@ng-select/ng-select';
import {FilterregelEditComponent} from './filterregel-edit.component';
import {FiltertoolPersoneelslabelRegel} from '../../../services/filtertool/regels/filtertool-personeelslabelregel';
import {FiltertoolRegelTypeNamen} from '../../../services/filtertool/filtertool-regels';
import {Personeel} from '../../../models/personeel';
import {FilterregelActiesComponent} from './filterregel-acties.component';

/**
 * Editor voor personeelslabelregels: selecteer 1 of meer labels (multiselect) en beheer verwijderen.
 */
@Component({
  standalone: true,
  selector: 'filterregel-personeelslabel-edit',
  imports: [CommonModule, FormsModule, NgSelectModule, FilterregelActiesComponent],
  templateUrl: './filterregel-personeelslabel-edit.component.html',
  styleUrls: ['./filterregel-personeelslabel-edit.component.scss', './filterregel-editors.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FilterregelPersoneelslabelEditComponent extends FilterregelEditComponent<FiltertoolPersoneelslabelRegel> implements OnInit {
  // Inputs (properties)
  /** De te bewerken personeelslabel-regel. */
  @Input({required: true}) override regel!: FiltertoolPersoneelslabelRegel;

  // Properties
  /** Mapping met label/omschrijving per regeltype voor weergave. */
  public readonly regelTypeNamen = FiltertoolRegelTypeNamen;
  /** Alle beschikbare labels (lookup). */
  public alleLabels: string[] = [];
  /** Lokale binding van gekozen labelnamen voor ngModel. */
  public gekozenLabels: string[] = [];

  constructor() {
    super('debug');
  }

  // Lifecycle hooks
  async ngOnInit(): Promise<void> {
    // init selectie
    this.gekozenLabels = Array.isArray(this.regel?.labelNamen) ? [...this.regel.labelNamen] : [];
    // haal alle labels op via modelhelper
    this.alleLabels = await Personeel.alleLabels();
    // trigger hertekenen omdat OnPush + @if anders niet update na async load
    this.cd.markForCheck();
    this.log.debug('Labels geladen', this.alleLabels?.length);
  }

  // Public methods
  /**
   * Slaat gewijzigde selectie op in de regel en emit de wijziging.
   */
  public labelsGewijzigd(): void {
    const clean = (this.gekozenLabels || []).filter(v => !!v && String(v).trim().length);
    if (this.regel) {
      this.regel.labelNamen = [...clean];
      this.wijzig.emit(this.regel);
    }
    try {
      this.cd.detectChanges();
    } catch {
      this.cd.markForCheck();
    }
  }
}
